import { QueryLazyOptions } from "@apollo/client";
import ClearIcon from "@assets/images/clear2.svg";
import InfoIcon from "@assets/images/info.svg";
import { Box, Chip, Paper, Tooltip } from "@material-ui/core";
import { Theme, withStyles } from "@material-ui/core/styles";
import { useUser } from "@saleor/auth";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import {
  DistributionChannelType,
  DomesticSoldToFilterInput,
  Exact,
  ExportShipToFilterInput,
  ExportSoldToFilterInput
} from "@saleor/graphql";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";

import useStyles from "./styles";

interface IProps {
  value?: string[] | string;
  keyFilterApi?: string;
  handleSelectedSearch?: (value: string[]) => void;
  inputValue?: string;
  setInputValue?: React.Dispatch<React.SetStateAction<string>>;
  placeholder?: string;
  first?: number;
  reRender?: boolean;
  headerText?: string;
  message?: string;
  contained?: boolean;
  name: string;
  getDataSuggestions: (
    options?: QueryLazyOptions<
      Exact<{
        filter?:
          | ExportShipToFilterInput
          | ExportSoldToFilterInput
          | DomesticSoldToFilterInput;
        first?: number;
        distributionChannelType?: DistributionChannelType;
        includeOneTimeCustomer?: boolean;
      }>
    >
  ) => void;
  fetchDataSuggestions?: any;
  loadingDataSuggestion: boolean;
  dataOptions: any[];
  error?: boolean;
  textError?: React.ReactNode;
  fullWidthItem?: boolean;
  getByValue?: boolean;
  showCount?: boolean;
  maxCount?: number;
  isCheckingRole?: boolean;
  isIncludeOneTimeCustomer?: boolean;
  id?: string;
  testId?: string;
}

interface ChipData {
  key: string;
  label: string;
  value: string;
}

const CustomTooltip = withStyles(
  (theme: Theme) => ({
    tooltip: {
      backgroundColor: theme.palette.grey[600],
      color: "#FFFFFF",
      fontSize: "1.2rem",
      maxWidth: "none"
    }
  }),
  { name: "index" }
)(Tooltip);

const MultipleAutocompleteTextInput: React.FC<IProps> = (props: IProps) => {
  const {
    value: initValue,
    handleSelectedSearch,
    placeholder,
    first,
    headerText,
    reRender,
    message,
    contained,
    getDataSuggestions,
    fullWidthItem,
    loadingDataSuggestion,
    keyFilterApi,
    dataOptions,
    error,
    textError,
    getByValue,
    showCount,
    maxCount,
    isCheckingRole,
    isIncludeOneTimeCustomer,
    name,
    id,
    testId
  } = props;

  const classes = useStyles();
  const [value, setValue] = useState<string>("");
  const [isReset, setIsReset] = useState<boolean>(false);
  const [arr, setArr] = useState<ChipData[]>([]);
  const { zone } = useUser();

  const handleChange = event => {
    setValue(event);
  };

  const handleOnSelectedSearch = value => {
    if (!value || arr.find(item => item.key === value)) {
      setIsReset(prevState => !prevState);
      return;
    }

    let valueToArr = value;
    if (getByValue) {
      valueToArr =
        dataOptions.find(item => item.title === value)?.value || null;
      if (!valueToArr) {
        return;
      }
    } else {
      valueToArr =
        dataOptions.find(item => item.title === value)?.title || null;
      if (!valueToArr) {
        return;
      }
    }
    setIsReset(prevState => !prevState);
    const arrClone = [...arr];
    if (maxCount) {
      if (arrClone.length === maxCount) {
        return;
      }
    }
    arrClone.push({
      key: value,
      label: value,
      value: valueToArr?.toString()
    });
    setArr(arrClone);
    handleSelectedSearch(
      arrClone.map(item => (getByValue ? item.value : item.label))
    );
  };

  const handleDelete = (chipToDelete: ChipData) => () => {
    const newArr = arr.filter(chip => chip.key !== chipToDelete.key);
    setArr(newArr);
    handleSelectedSearch(
      newArr.map(item => (getByValue ? item.value : item.label))
    );
  };

  useEffect(() => {
    if (value.length >= 2) {
      const filter = {
        [keyFilterApi]: value
      };
      getDataSuggestions({
        variables: {
          first: first || 100,
          filter,
          includeOneTimeCustomer: isIncludeOneTimeCustomer,
          distributionChannelType: isCheckingRole
            ? (zone?.toUpperCase() as DistributionChannelType)
            : undefined
        }
      });
    }
  }, [value]);

  useEffect(() => {
    if (initValue && Array.isArray(initValue)) {
      setArr(
        initValue.map(item => ({
          key: item,
          label: item,
          value: item
        }))
      );
    }
  }, [initValue, dataOptions]);

  useEffect(() => {
    setValue("");
    setArr([]);
    setIsReset(prevState => !prevState);
  }, [reRender]);

  const renderEmptyBox = () => (
    <>
      <div className={classes.boxList}>
        <MyTypography className={classes.headerTxt} variant={"body3"}>
          {headerText} {showCount ? `(${arr.length})` : ""}
        </MyTypography>
        {message ? (
          <Box className={classes.info}>
            <SVG
              width={18}
              height={18}
              src={InfoIcon}
              className={classes.icon}
            />
            <MyTypography variant="caption" className={classes.caption}>
              {message}
            </MyTypography>
          </Box>
        ) : null}
      </div>
    </>
  );

  return (
    <>
      <Autocomplete
        resetAlways
        contained={contained}
        optionsProp={dataOptions}
        placeholderProps={placeholder}
        onChangeProps={handleChange}
        loading={loadingDataSuggestion}
        onSearch={handleOnSelectedSearch}
        isReset={isReset}
        error={error}
        disabled={arr.length === maxCount}
        notUseFilterOptions
        id={id}
        testId={testId}
        name={name}
      />
      {error && textError ? textError : ""}
      {arr.length > 0 ? (
        <div className={classes.chipBox}>
          <MyTypography className={classes.chipText} variant={"body3"}>
            {headerText} {showCount ? `(${arr.length})` : ""}
          </MyTypography>
          <Paper component="ul" className={classes.chipPaper}>
            {arr.map(data => (
              <li
                key={data.key}
                className={classNames(
                  classes.chipLi,
                  fullWidthItem && classes.fullWidth
                )}
              >
                <CustomTooltip title={data.label} placement="top">
                  <Chip
                    deleteIcon={
                      <img
                        className={classes.deleteIcon}
                        src={ClearIcon}
                        alt={"delete"}
                      />
                    }
                    label={data.label}
                    onDelete={handleDelete(data)}
                    className={classNames(
                      classes.chip,
                      fullWidthItem && classes.fullWidth
                    )}
                  />
                </CustomTooltip>
              </li>
            ))}
          </Paper>
        </div>
      ) : (
        renderEmptyBox()
      )}
    </>
  );
};

export default MultipleAutocompleteTextInput;
